import { EActionType, ECategoryType, EConfirmDialog, EViews } from '../../../data/Constants';
import {
  checkIfSectionIsSubmodel,
  checkIsFeatureActive,
  findSectionById,
  getUpdatedCategoryData,
  getViewSaveRequest,
} from '../../../services/CategoryHelper';
import {
  getFeatureFamilyList,
  getViewData,
  saveView,
  showAddCategoryDialog,
  updateCategoryModify,
  updateView,
} from '../../../store/states/CategorySlice';
import {
  deleteSectionRecursive,
  findFirstAvailableSectionId,
  isItemAssignedToSection,
  removeInactiveFeatures,
} from './categoryTreeUtils';
import { INITIAL_CONFIRM_DIALOG } from '../../../../types/ICategoryTypes';

import type { Dispatch, SetStateAction } from 'react';
import type { AppDispatch, AppState } from '../../../store/AppStore';
import type { IFeatureFamily, ISection } from '../../../../types';
import type {
  ICategoryConfirmAction,
  ICategoryConfirmDialogInfo,
} from '../../../../types/ICategoryTypes';

interface IBaseParams {
  categoryState: AppState['category'];
  dispatch: AppDispatch;
  sections: ISection[];
}

interface IRemoveItemParams extends IBaseParams {
  itemCode: string;
  selectedSectionId: string | null;
  setGlobalUnassignedItems: Dispatch<SetStateAction<IFeatureFamily[]>>;
  setSections: Dispatch<SetStateAction<ISection[]>>;
}

interface ISaveParams extends IBaseParams {
  productId?: string;
  setEditDataView: Dispatch<SetStateAction<boolean>>;
}

interface IAddSectionParams extends IBaseParams {
  parent?: ISection | null;
  productId?: string;
}

interface IConfirmActionParams extends IBaseParams {
  calculateUnAssigned: (sectionsToProcess: ISection[]) => void;
  confirm: ICategoryConfirmAction;
  productId?: string;
  sectionToDelete: ISection | null;
  selectedSectionId: string | null;
  setConfirmDelete: Dispatch<SetStateAction<ICategoryConfirmDialogInfo>>;
  setEditDataView: Dispatch<SetStateAction<boolean>>;
  setSectionToDelete: Dispatch<SetStateAction<ISection | null>>;
  setSelectedSectionId: Dispatch<SetStateAction<string | null>>;
}

export const removeCategoryItem = ({
  categoryState,
  dispatch,
  itemCode,
  sections,
  selectedSectionId,
  setGlobalUnassignedItems,
  setSections,
}: IRemoveItemParams): void => {
  if (!selectedSectionId) {
    return;
  }
  const newSections = sections;
  const targetSection = findSectionById(newSections, selectedSectionId);
  if (!targetSection) {
    return;
  }
  targetSection.contents = targetSection.contents?.filter((item) => item.code !== itemCode) ?? [];
  const isAssignedElsewhere = isItemAssignedToSection(
    newSections.filter((section) => section.code !== selectedSectionId),
    itemCode
  );
  setSections(newSections);
  dispatch(updateCategoryModify());
  if (!isAssignedElsewhere) {
    const feature = categoryState.featureFamily.find((item) => item.code === itemCode);
    if (feature) {
      setGlobalUnassignedItems((prevItems) => [...prevItems, feature]);
    }
  }
};

export const saveCategoryView = ({
  categoryState,
  dispatch,
  productId,
  sections,
  setEditDataView,
}: ISaveParams): void => {
  if (!productId) {
    return;
  }
  const cleanedSections = removeInactiveFeatures(sections, (content) =>
    checkIsFeatureActive(content.code, categoryState.featureFamily)
  );
  const updatedData = getViewSaveRequest(productId, cleanedSections, {
    code: categoryState.view.code.split('_')[1] ?? EViews.Master,
    description: categoryState.view.description || EViews.Master,
  });
  const isValid = updatedData.sections.every(
    (section) =>
      checkIfSectionIsSubmodel(section as ISection) ||
      (Array.isArray(section.contentCodes) && Array.isArray(section.sections))
  );
  if (!isValid) {
    return;
  }
  dispatch(saveView(updatedData)).then((action) => {
    if (saveView.fulfilled.match(action) && action.payload === updatedData) {
      setEditDataView(false);
    }
  });
};

export const addCategorySection = ({
  categoryState,
  dispatch,
  parent = null,
  productId,
  sections,
}: IAddSectionParams): void => {
  if (!productId) {
    return;
  }
  const updatedData = getUpdatedCategoryData(productId, sections);
  dispatch(
    updateView({
      ...updatedData,
      code: categoryState.view.code,
      description: categoryState.view.description,
    })
  );
  if (parent) {
    const section: ISection = {
      ...parent,
      hideGeneral: parent.hideGeneral || false,
      action: parent.type === ECategoryType.MultiNumeric ? EActionType.Edit : EActionType.Add,
      type: parent.type === ECategoryType.MultiNumeric ? ECategoryType.MultiNumeric : parent.type,
    };
    dispatch(showAddCategoryDialog({ section, parent }));
  } else {
    dispatch(showAddCategoryDialog({ parent: null }));
  }
};

const reloadCategoryData = (
  dispatch: AppDispatch,
  productId: string | undefined,
  setEditDataView: Dispatch<SetStateAction<boolean>>
): void => {
  if (!productId) {
    return;
  }
  dispatch(getViewData({ productCode: productId }));
  dispatch(
    getFeatureFamilyList({
      productCode: productId,
      includeScopeFeatures: false,
      workItemNum: '',
      page: 0,
      limit: 0,
    })
  );
  setEditDataView(false);
};

export const confirmCategoryAction = ({
  calculateUnAssigned,
  confirm,
  dispatch,
  productId,
  sectionToDelete,
  sections,
  selectedSectionId,
  setConfirmDelete,
  setEditDataView,
  setSectionToDelete,
  setSelectedSectionId,
}: IConfirmActionParams): void => {
  if (confirm.status && confirm.alertFor === EConfirmDialog.DeleteSection && sectionToDelete) {
    const updatedSections = deleteSectionRecursive(sections, sectionToDelete.code);
    const nextSelectedId =
      selectedSectionId === sectionToDelete.code
        ? findFirstAvailableSectionId(updatedSections)
        : selectedSectionId;
    setSelectedSectionId(nextSelectedId);
    calculateUnAssigned(updatedSections);
    dispatch(updateCategoryModify());
  } else if (confirm.status && confirm.alertFor === EConfirmDialog.DiscardChanges) {
    reloadCategoryData(dispatch, productId, setEditDataView);
  }
  setConfirmDelete(INITIAL_CONFIRM_DIALOG);
  setSectionToDelete(null);
};
